import { c as _c } from "react/compiler-runtime";
import { type Address, getChainAddresses, type MarketParams, MathLib } from '@morpho-org/blue-sdk';
import { ZERO } from '@morpho-org/dapp-core';
import { isDefined, Time } from '@morpho-org/morpho-ts';
import { useTokenBalance } from '@repo/web3';
import { useCallback, useMemo, useState } from 'react';
import { isAddressEqual } from 'viem';
import { useBalance } from 'wagmi';
import { useWeb3Context } from '@/contexts/Web3Context';
import { useSpendableBalance } from '@/hooks/useSpendableBalance';
import { useWrappingGasReserve } from '@/hooks/useWrappingGasReserve';
import { computeWrappableNativeBalance } from '@/hooks/wrappingGasReserve.helpers';
import { computeNativeWrapSplit } from './marketAction.helpers';
import { ACCRUAL_BUFFER, computeMaxRepayAssets } from './marketMaxAmounts';
import { useMarketBlue } from './useMarketBlue';
export interface UseMarketRepayNativeWrapParams {
  /** Target market params (for the fresh position + gas-reserve representative repay). */
  marketParams: MarketParams | undefined;
  /** Loan token address (checked against the chain's wNative). */
  loanToken: Address | undefined;
  /** User-entered total to repay (ERC20 + native), in loan-token assets. */
  totalRepay: bigint;
  /** Whether the entered amount is a full close (repay exact shares). */
  isMaxRepay: boolean;
  /** `marketRepayWithdraw` flow flag — gates max and balance resolution. */
  enabled: boolean;
  /** Gates the native-wrap UI/balances (folded into `marketRepayWithdraw`). */
  wrapEnabled: boolean;
}

/**
 * Native-ETH wrapping for the market-actions-v2 "repay" path, the repay sister of
 * {@link useMarketCollateralNativeWrap}. `repay` / `repayWithdrawCollateral`
 * accept an explicit `nativeAmount` (only on a wrapped-native loan market — they throw
 * otherwise), so this:
 * - detects the wNative-loan case,
 * - reserves gas before wrapping (`useWrappingGasReserve`) so MAX never wraps the ETH the
 *   wallet needs to pay for the transaction,
 * - funds the entire entered leg from either ERC20 wNative or native ETH
 *   (`computeNativeWrapSplit`), depending on the selected tab, and
 * - sizes the native leg against the amount the SDK will actually pull: a partial repay
 *   pulls the entered amount, a full close pulls the **accrual-buffered** transfer
 *   (`accrueInterest(now|lastUpdate + ACCRUAL_BUFFER).toBorrowAssets(shares, "Up")`) — the exact
 *   value the entity computes for shares-mode repay — so wrapping funds the whole transfer
 *   even as interest accrues between quote and execution.
 *
 * The MAX value is per tab: the wrapped tab uses the ERC20 wNative balance, while the native tab
 * uses the wrappable ETH balance. Returns inert values (`nativeAmount: 0n`, `maxRepay: undefined`)
 * when the v2 flow is off, when the loan token isn't wNative, or before balances resolve.
 */
export function useMarketRepayNativeWrap(t0) {
  const $ = _c(49);
  const {
    marketParams,
    loanToken,
    totalRepay,
    isMaxRepay,
    enabled,
    wrapEnabled
  } = t0;
  const {
    account,
    targetChainId
  } = useWeb3Context();
  const [isWrapping, setIsWrapping] = useState(false);
  const {
    positionData
  } = useMarketBlue(enabled ? marketParams : undefined);
  let t1;
  bb0: {
    if (!enabled || !wrapEnabled || !loanToken) {
      t1 = false;
      break bb0;
    }
    let t2;
    if ($[0] !== loanToken || $[1] !== targetChainId) {
      const {
        wNative
      } = getChainAddresses(targetChainId);
      t2 = !!wNative && isAddressEqual(loanToken, wNative);
      $[0] = loanToken;
      $[1] = targetChainId;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    t1 = t2;
  }
  const isWNativeLoan = t1;
  let t2;
  if ($[3] !== account.address || $[4] !== enabled || $[5] !== loanToken || $[6] !== targetChainId) {
    t2 = {
      chainId: targetChainId,
      userAddress: account.address,
      tokenAddress: loanToken,
      enabled,
      refetchInterval: 10000
    };
    $[3] = account.address;
    $[4] = enabled;
    $[5] = loanToken;
    $[6] = targetChainId;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const {
    data: erc20Balance,
    isLoading: isLoadingErc20Balance
  } = useTokenBalance(t2);
  const t3 = enabled && account.isConnected;
  let t4;
  if ($[8] !== erc20Balance || $[9] !== loanToken || $[10] !== t3 || $[11] !== targetChainId) {
    t4 = {
      chainId: targetChainId,
      token: loanToken,
      balance: erc20Balance,
      enabled: t3
    };
    $[8] = erc20Balance;
    $[9] = loanToken;
    $[10] = t3;
    $[11] = targetChainId;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const {
    spendableBalance: spendableErc20Balance,
    isLoading: isLoadingSpendableBalance
  } = useSpendableBalance(t4);
  const t5 = isWNativeLoan && account.isConnected;
  let t6;
  if ($[13] !== t5) {
    t6 = {
      enabled: t5,
      refetchInterval: 10000
    };
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== account.address || $[16] !== t6 || $[17] !== targetChainId) {
    t7 = {
      address: account.address,
      chainId: targetChainId,
      query: t6
    };
    $[15] = account.address;
    $[16] = t6;
    $[17] = targetChainId;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const {
    data: nativeBalanceData,
    isLoading: isLoadingNativeBalance
  } = useBalance(t7);
  const nativeBalance = nativeBalanceData?.value ?? ZERO;
  let t8;
  bb1: {
    if (!positionData) {
      t8 = undefined;
      break bb1;
    }
    let t9;
    if ($[19] !== positionData.market.lastUpdate) {
      t9 = MathLib.max(Time.timestamp(), positionData.market.lastUpdate);
      $[19] = positionData.market.lastUpdate;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    const accrualTimestamp = t9 + ACCRUAL_BUFFER;
    let t10;
    if ($[21] !== accrualTimestamp || $[22] !== positionData.borrowShares || $[23] !== positionData.market) {
      t10 = positionData.market.accrueInterest(accrualTimestamp).toBorrowAssets(positionData.borrowShares, "Up");
      $[21] = accrualTimestamp;
      $[22] = positionData.borrowShares;
      $[23] = positionData.market;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    t8 = t10;
  }
  const fullCloseNeed = t8;
  const t9 = isWNativeLoan && account.isConnected;
  let t10;
  if ($[25] !== t9 || $[26] !== targetChainId) {
    t10 = {
      chainId: targetChainId,
      enabled: t9
    };
    $[25] = t9;
    $[26] = targetChainId;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const {
    reserve: nativeGasReserve,
    isLoading: isLoadingGasReserve,
    isError: isGasReserveError
  } = useWrappingGasReserve(t10);
  let t11;
  if ($[28] !== nativeBalance || $[29] !== nativeGasReserve) {
    t11 = computeWrappableNativeBalance({
      nativeBalance,
      nativeGasReserve
    });
    $[28] = nativeBalance;
    $[29] = nativeGasReserve;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const wrappableNativeBalance = t11;
  const repayNeed = !isMaxRepay || fullCloseNeed == null ? totalRepay : fullCloseNeed;
  const t12 = isWrapping && isWNativeLoan;
  let t13;
  if ($[31] !== repayNeed || $[32] !== t12) {
    t13 = computeNativeWrapSplit({
      totalAmount: repayNeed,
      isWrapping: t12
    });
    $[31] = repayNeed;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const {
    nativeAmount
  } = t13;
  const debt = positionData?.borrowAssets;
  let t14;
  bb2: {
    if (!enabled || !isDefined(debt) || !isDefined(fullCloseNeed) || !isDefined(spendableErc20Balance)) {
      t14 = undefined;
      break bb2;
    }
    const available = isWrapping && isWNativeLoan ? wrappableNativeBalance : spendableErc20Balance;
    let t15;
    if ($[34] !== available || $[35] !== debt || $[36] !== fullCloseNeed) {
      t15 = computeMaxRepayAssets({
        debt,
        fullCloseNeed,
        available
      });
      $[34] = available;
      $[35] = debt;
      $[36] = fullCloseNeed;
      $[37] = t15;
    } else {
      t15 = $[37];
    }
    t14 = t15;
  }
  const maxRepay = t14;
  let t15;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = value => setIsWrapping(value);
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const onWrappingChange = t15;
  const t16 = enabled && account.isConnected && (isLoadingErc20Balance || isLoadingSpendableBalance);
  const t17 = isLoadingNativeBalance || isLoadingGasReserve;
  const t18 = isWrapping && isGasReserveError && repayNeed > (erc20Balance ?? ZERO) ? "Pay with the wrapped token to continue" : undefined;
  let t19;
  if ($[39] !== debt || $[40] !== isWNativeLoan || $[41] !== isWrapping || $[42] !== maxRepay || $[43] !== nativeAmount || $[44] !== t16 || $[45] !== t17 || $[46] !== t18 || $[47] !== wrappableNativeBalance) {
    t19 = {
      isWNativeLoan,
      isWrapping,
      onWrappingChange,
      wrappableNativeBalance,
      nativeAmount,
      maxRepay,
      debt,
      isMaxLoading: t16,
      isWrappingToggleLoading: t17,
      wrapErrorMessage: t18
    };
    $[39] = debt;
    $[40] = isWNativeLoan;
    $[41] = isWrapping;
    $[42] = maxRepay;
    $[43] = nativeAmount;
    $[44] = t16;
    $[45] = t17;
    $[46] = t18;
    $[47] = wrappableNativeBalance;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  return t19;
}